import { NgModule, inject } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { BrowserPageTitle } from '../shared/constants/browser-page-title.enum';
import { OrderPage } from './order.page';
import { OrderPageResolverResolver } from './services/order-page-resolver.resolver';
import { SubmittedOrderResolverService } from './services/order-submitted-resolver.service';
import { SplitGuardService } from '@shared/services/authentication/feature-flag-guard.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { PATHS } from '@shared/constants/paths';
import { superUserGuard } from '@shared/guards/super-user.guard';
import { OrderInsightsResolverService } from './services/order-insights-resolver.service';
import { splitWithUserGuard } from '@shared/guards/split-with-user.guard';
import { vsOrderGuard } from '@shared/services/authentication/vs-order-guard';

const routes: Routes = [
  {
    path: '',
    component: OrderPage,
    data: { title: BrowserPageTitle.reviewOrder },
    canDeactivate: [vsOrderGuard],
    resolve: {
      order: OrderPageResolverResolver,
    },
  },
  {
    path: 'submitted-order/:orderId',
    loadChildren: () =>
      import('./pages/order-submitted/order-submitted.page.module').then(
        m => m.OrderSubmittedPageModule,
      ),
    resolve: {
      order: SubmittedOrderResolverService,
    },
    data: { title: BrowserPageTitle.submittedOrder },
  },
  {
    path: 'my-orders',
    loadChildren: () =>
      import('../my-orders/pages/order-status/order-status.module').then(
        m => m.OrderStatusPageModule,
      ),
    data: { title: BrowserPageTitle.myOrders },
  },
  {
    path: 'order-confirmation/:orderId',
    loadChildren: () =>
      import('./pages/order-confirmation/order-confirmation.module').then(
        m => m.OrderConfirmationPageModule,
      ),
    data: { title: BrowserPageTitle.orderSubmitted },
  },
  {
    path: 'order-exceptions/:orderId',
    loadChildren: () =>
      import('./pages/order-exceptions/order-exceptions.module').then(
        m => m.OrderExceptionsPageModule,
      ),
    resolve: {
      order: SubmittedOrderResolverService,
    },
  },
  {
    path: 'order-exceptions',
    loadChildren: () =>
      import('./pages/order-exceptions/order-exceptions.module').then(
        m => m.OrderExceptionsPageModule,
      ),
    resolve: {
      order: SubmittedOrderResolverService,
    },
  },
  {
    path: 'image-ordering',
    loadChildren: () =>
      import('./pages/image-ordering/image-ordering.module').then(
        m => m.ImageOrderingPageModule,
      ),
  },
  {
    path: 'imported-orders',
    loadChildren: () =>
      import('./pages/imported-orders/imported-orders.module').then(
        m => m.ImportedOrdersPageModule,
      ),
    data: SplitGuardService.routeData(
      [FEATURES.split_global_imported_orders.name],
      PATHS.HOME,
    ),
    canActivate: [
      route => inject(SplitGuardService).canActivate(route),
      superUserGuard(PATHS.HOME, false, [
        FEATURES.split_global_order_super_user_import_order.name,
      ]),
    ],
  },
  {
    path: 'imported-orders/:importOrderIdentifier',
    loadChildren: () =>
      import('./pages/imported-orders/imported-orders.module').then(
        m => m.ImportedOrdersPageModule,
      ),
    data: SplitGuardService.routeData(
      [FEATURES.split_global_imported_orders.name],
      PATHS.HOME,
    ),
    canActivate: [
      route => inject(SplitGuardService).canActivate(route),
      superUserGuard(PATHS.HOME, false, [
        FEATURES.split_global_order_super_user_import_order.name,
      ]),
    ],
  },
  {
    path: 'import-order-validation',
    loadChildren: () =>
      import(
        './pages/import-order-validation/import-order-validation.module'
      ).then(m => m.ImportOrderPageModule),
    data: SplitGuardService.routeData(
      [FEATURES.split_global_import_order.name],
      PATHS.HOME,
    ),
    canActivate: [
      route => inject(SplitGuardService).canActivate(route),
      superUserGuard(PATHS.HOME, false, [
        FEATURES.split_global_order_super_user_import_order.name,
      ]),
    ],
  },
  {
    path: 'quick-order-entry',
    loadChildren: () =>
      import('./pages/quick-order-entry/quick-order-entry.module').then(
        m => m.QuickOrderEntryPageModule,
      ),
  },
  {
    path: 'order-insights/:orderId',
    loadChildren: () =>
      import('./pages/order-insights/order-insights.module').then(
        m => m.OrderInsightsPageModule,
      ),
    resolve: {
      OrderInsightsResolverService,
    },
    data: { title: BrowserPageTitle.orderInsights },
  },
  {
    path: 'image-ordering',
    loadChildren: () =>
      import('./pages/image-ordering/image-ordering.module').then(
        m => m.ImageOrderingPageModule,
      ),
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class OrderPageRoutingModule {}
